import {
  Box, Button, Flex,
  Input, InputDescription, InputLabel,
  PhoneNumberInput, Text
} from '@huspy/briks-web';
import Select, { SelectItem } from '@components/v2/Select';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { useTranslation } from 'react-i18next';
import { useForm, zodResolver } from '@mantine/form';
import { scrollToError } from '@shared/utils';
import { Check, Plus } from '@huspy/briks-icons';
import {
  useEffect, useMemo, useRef
} from 'react';
import useCreateLead from '@modules/refer-and-earn/api/mutation/useCreateLead';
import { useDisclosure } from '@mantine/hooks';
import { createLeadSchema } from '@modules/refer-and-earn/presentation/pages/List/features/ReferAndEarnSlido/validator';
import { responseCapturedComponentStyles } from '@modules/refer-and-earn/presentation/pages/List/features/ReferAndEarnSlido/styles';
import { slidoDeepLink } from '@modules/core/utils';
import { useLocation } from '@tanstack/react-router';
import { USER_EVENTS } from '@shared/analytics/events';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';

const SuccessResponseComponent = () => {
  const { t } = useTranslation();
  return (
    <Flex direction='column' gap='2' p='6' pb='0' mt='70' className={ responseCapturedComponentStyles.text }>
      <Check
        width={ 100 }
        height={ 100 }
        className={ responseCapturedComponentStyles.icon }
      />
      <Text size='3xl'>
        {t('referAndEarn.slido.finished.title')}
      </Text>
      <Text size='lg' color='neutral.500'>
        {t('referAndEarn.slido.finished.lead_referred')}
      </Text>
      <Text size='lg' color='neutral.500'>
        {t('referAndEarn.slido.finished.lead_received')}
      </Text>
      <Text size='lg' color='neutral.500'>
        {t('referAndEarn.slido.finished.contact_soon')}
      </Text>
    </Flex>
  );
};

export const ReferAndEarnSlido = () => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const [opened, { open, close }] = useDisclosure(hash === slidoDeepLink.REFER_AND_EARN);
  const hasTrackedView = useRef(false);
  const {
    mutateAsync,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = useCreateLead();
  const form = useForm({
    initialValues: {
      name: '',
      phone: '',
      city: 'Dubai',
      marketType: 'primary',
      budget: '',
      community: '',
    },
    validate: zodResolver(createLeadSchema),
  });
  const formErrors = useMemo(() => form.errors, [form.errors]);
  const isLeadSubmittedSuccessfully = useMemo(() => isSuccess && !isPending, [isSuccess, isPending]);

  useEffect(() => {
    if (opened && !hasTrackedView.current) {
      trackAmplitudeEvent(USER_EVENTS.REFER_AND_EARN.SLIDO_VIEWED);
      hasTrackedView.current = true;
    }
  }, [opened]);

  const onSubmit = async () => {
    form.validate();
    if (!form.isValid()) {
      scrollToError(form.errors);
      return;
    }
    await mutateAsync(form.values);
  };

  const handleOnClose = () => {
    form.reset();
    close();
    resetMutation();
  };

  return (
    <>
      <BaseSlido
        opened={ opened }
        close={ handleOnClose }
        title={ (
          <Flex direction='column' gap='2' p='6' pb='0'>
            <Text size='5xl'>
              {t('referAndEarn.slido.title')}
            </Text>
          </Flex>
        ) }
        footer={ (
          <Flex justify='end' gap='2'>
            {
              isLeadSubmittedSuccessfully ? (
                <Button
                  type='submit'
                  data-test='done-btn'
                  onClick={ handleOnClose }
                >
                  {t('referAndEarn.slido.main.actions.done')}
                </Button>
              ) : (
                <>
                  <Button
                    type='button'
                    onClick={ handleOnClose }
                    variant='secondary'
                    data-test='discard-btn'
                    data-testid='discard-btn'
                  >
                    {t('referAndEarn.slido.main.actions.discard')}
                  </Button>
                  <Button
                    type='submit'
                    disabled={ isPending }
                    loading={ isPending }
                    variant={ isPending ? 'secondary' : 'primary' }
                    data-test='save-btn'
                    data-testid='save-btn'
                    onClick={ onSubmit }
                  >
                    {t('referAndEarn.slido.main.actions.create')}
                  </Button>
                </>
              )
            }
          </Flex>
        ) }
      >
        <>
          {
            isLeadSubmittedSuccessfully
              ? <SuccessResponseComponent />
              : (
                <Flex direction='column' gap='5' p='6' pb='0'>
                  <Flex>
                    <Box w='50%' pr='4'>
                      <InputLabel isError={ !!formErrors.name }>
                        {t('common.forms.name.label')}
                      </InputLabel>
                      <Input
                        type='text'
                        onChange={ (e) => form.getInputProps('name').onChange(e.currentTarget.value) }
                        isError={ !!formErrors.name }
                        placeholder={ t('common.forms.name.placeholder') }
                      />
                      <InputDescription mt='1' isError={ !!formErrors.name }>
                        {formErrors.name ?? ''}
                      </InputDescription>
                    </Box>
                    <Box w='50%' pl='2'>
                      <InputLabel isError={ !!formErrors.phone }>
                        {t('common.forms.mobile.label')}
                      </InputLabel>
                      <PhoneNumberInput
                        name='mobile'
                        defaultCountry={ t('common.forms.mobile.defaultCountryPrefix') }
                        placeholder={ t('common.forms.mobile.placeholder') }
                        disabled={ isPending }
                        onChange={ (e) =>
                          form.getInputProps('phone').onChange(e.phone) }
                        data-test='input-mobile'
                        isError={ !!formErrors.phone }
                      />
                      <InputDescription mt='1' isError={ !!formErrors.phone }>
                        {formErrors.phone ?? ''}
                      </InputDescription>
                    </Box>
                  </Flex>
                  <Flex>
                    <Box w='50%' pr='3'>
                      <InputLabel isError={ !!formErrors.name }>
                        {t('referAndEarn.slido.main.content.form.city.label')}
                      </InputLabel>
                      <Select
                        onValueChange={ (e) => {
                          form.getInputProps('city').onChange(e.value[0]);
                        } }
                        data={ [
                          { label: 'Dubai', value: 'Dubai' },
                          { label: 'Abu Dhabi', value: 'Abu Dhabi' }
                        ] as SelectItem[] }
                        defaultValue={ ['Dubai'] }
                        isError={ !!formErrors.city }
                      />
                      <InputDescription mt='1' isError={ !!formErrors.city }>
                        {formErrors.city ?? ''}
                      </InputDescription>
                    </Box>
                    <Box w='50%' pl='3'>
                      <InputLabel isError={ !!formErrors.marketType }>
                        {t('referAndEarn.slido.main.content.form.marketType.label')}
                      </InputLabel>
                      <Select
                        onValueChange={ (e) => {
                          form.getInputProps('marketType').onChange(e.value[0]);
                        } }
                        data={ [
                          {
                            label: t('referAndEarn.slido.main.content.form.marketType.options.primary'),
                            value: 'primary',
                          },
                          {
                            label: t('referAndEarn.slido.main.content.form.marketType.options.secondary'),
                            value: 'secondary',
                          },
                          {
                            label: t('referAndEarn.slido.main.content.form.marketType.options.leasing'),
                            value: 'leasing',
                          }
                        ] as SelectItem[] }
                        defaultValue={ ['primary'] }
                        isError={ !!formErrors.marketType }
                      />
                      <InputDescription mt='1' isError={ !!formErrors.marketType }>
                        {formErrors.marketType ?? ''}
                      </InputDescription>
                    </Box>
                  </Flex>
                  <Flex>
                    <Box w='50%' pr='3'>
                      <InputLabel isError={ !!formErrors.budget }>
                        {t('referAndEarn.slido.main.content.form.budget.label')}
                      </InputLabel>
                      <Input
                        type='text'
                        onChange={ (e) => form.getInputProps('budget').onChange(e.currentTarget.value) }
                        isError={ !!formErrors.budget }
                        placeholder={ t('referAndEarn.slido.main.content.form.budget.placeholder') }
                      />
                      <InputDescription mt='1' isError={ !!formErrors.budget }>
                        {formErrors.budget ?? ''}
                      </InputDescription>
                    </Box>
                    <Box w='50%' pl='3'>
                      <InputLabel isError={ !!formErrors.community }>
                        {t('referAndEarn.slido.main.content.form.community.label')}
                      </InputLabel>
                      <Input
                        type='text'
                        onChange={ (e) => form.getInputProps('community').onChange(e.currentTarget.value) }
                        isError={ !!formErrors.community }
                        placeholder={ t('referAndEarn.slido.main.content.form.community.placeholder') }
                      />
                      <InputDescription mt='1' isError={ !!formErrors.community }>
                        {formErrors.community ?? ''}
                      </InputDescription>
                    </Box>
                  </Flex>
                </Flex>
              )
          }
        </>
      </BaseSlido>
      <Button onClick={ open } data-test='create-refer-and-earn-btn' data-testid='create-refer-and-earn-btn'>
        <Plus width={ 24 } color='inherit' />
        {t('referAndEarn.referLeadButton')}
      </Button>
    </>
  );
};
